import { A11yBooleanInput } from '../interfaces/a11y.interfaces';

let nextA11yId = 0;

export function createA11yId(prefix: string): string {
  nextA11yId += 1;

  return `${prefix}-${nextA11yId}`;
}

export function normalizeAriaValue(value: string | null | undefined): string | null {
  return value?.trim() || null;
}

export function normalizeToken<T extends string>(
  value: string | null | undefined,
  allowedValues: readonly T[],
  defaultValue: T | null = null
): T | null {
  const normalizedValue = normalizeAriaValue(value);

  if (!normalizedValue) {
    return defaultValue;
  }

  return (allowedValues as readonly string[]).includes(normalizedValue)
    ? normalizedValue as T
    : defaultValue;
}

export function addAriaId(ids: string[], id: string | null | undefined): void {
  const normalizedId = normalizeAriaValue(id);

  if (normalizedId && !ids.includes(normalizedId)) {
    ids.push(normalizedId);
  }
}

export function removeAriaId(ids: string[], id: string | null | undefined): void {
  const normalizedId = normalizeAriaValue(id);

  if (!normalizedId) {
    return;
  }

  const index = ids.indexOf(normalizedId);

  if (index > -1) {
    ids.splice(index, 1);
  }
}

export function normalizeAriaIdList(ids: string[]): string | null {
  return normalizeAriaValue(ids.join(' '));
}

export function toBoolean(value: A11yBooleanInput, defaultValue = false): boolean {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }

  return value === true || value === 'true';
}

export function toAriaBoolean(value: A11yBooleanInput): 'true' | 'false' | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return toBoolean(value) ? 'true' : 'false';
}

export function toRequiredAriaBoolean(value: A11yBooleanInput): 'true' | 'false' {
  return toBoolean(value) ? 'true' : 'false';
}

export function isNativeFocusableControl(element: HTMLElement): boolean {
  const tagName = element.tagName.toLowerCase();

  if (tagName === 'button' || tagName === 'input' || tagName === 'select' || tagName === 'textarea') {
    return true;
  }

  return tagName === 'a' && element.hasAttribute('href');
}
